import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertCircle, User, KeyRound, Eye, EyeOff, HelpCircle, ExternalLink, CheckCircle2, LayoutGrid,
} from 'lucide-react';
import { useAccountSetup } from '../hooks/useAccountSetup';
import { useAuth } from '../contexts/AuthContext';
import { INDUSTRIES } from '../helpers/collections';
import { PersonalAccessTokenAPI } from '../api';
import { toast } from 'sonner';
import orbitLogo from '/images/logo.png';
import orbitDarkLogo from '/images/logo-dark.png';
import { useTheme } from 'next-themes';
import { MiniFooter } from './MiniFooter';
import { Welcome } from '../pages/Welcome';

// Same validation Settings.tsx uses, so onboarding rejects the same bad input.
const GITHUB_PAT_PATTERN = /^(ghp_[A-Za-z0-9]{36}|github_pat_[A-Za-z0-9_]{22,})$/;

// Step 1 renders the standalone Welcome page (its own layout, no card/stepper).
// Steps 2-4 share the wizard shell below.
const STEP_WELCOME = 1;
const STEP_TEAM = 2;
const STEP_GITHUB_TOKEN = 3;
const STEP_READY = 4;

const STEPPER_STEPS = [STEP_TEAM, STEP_GITHUB_TOKEN, STEP_READY];

const STEP_HEADINGS: Record<number, { title: string; subtitle: string }> = {
  [STEP_TEAM]: {
    title: 'Tell Us About Yourself',
    subtitle: "A couple of quick questions to set up your workgroup",
  },
  [STEP_GITHUB_TOKEN]: {
    title: 'Connect Your GitHub Token',
    subtitle: 'Save it now, or add it later from Settings',
  },
  [STEP_READY]: {
    title: 'Your Workgroup Is Ready',
    subtitle: "You're all set to start using ORbiT8",
  },
};

export function AccountSetup({ onComplete }: { onComplete: () => void }) {
  const { createCompany, getPackages } = useAccountSetup();
  const { user, workgroups, refreshUser } = useAuth();
  const { theme } = useTheme();

  const [currentStep, setCurrentStep] = useState(STEP_WELCOME);

  const personalWorkgroup = workgroups.find((w) => w.workgrouptype === 'PERSONAL');
  const [teamData, setTeamData] = useState({
    title: personalWorkgroup?.title || (user?.firstname ? `${user.firstname}'s Workgroup` : ''),
    industry: '',
    usecase: '',
    description: '',
  });
  const [teamErrors, setTeamErrors] = useState<Record<string, string>>({});
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  const [patValue, setPatValue] = useState('');
  const [showPat, setShowPat] = useState(false);
  const [patError, setPatError] = useState<string | null>(null);
  const [isSavingPat, setIsSavingPat] = useState(false);
  const [connectedUsername, setConnectedUsername] = useState<string | null>(null);
  const [showPatHelpDialog, setShowPatHelpDialog] = useState(false);

  const handleSaveTeam = async () => {
    const errors: Record<string, string> = {};
    if (!teamData.industry) errors.industry = 'Please select your profession/field';
    if (!teamData.usecase) errors.usecase = 'Please select a use case';
    setTeamErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // The name field is locked, but guard against an empty default anyway
    // (e.g. the personal workgroup hasn't loaded yet) rather than sending a
    // blank title to createOrganisation.
    const workgroupTitle = teamData.title.trim() || `${user?.firstname || 'My'}'s Workgroup`;

    setIsSavingTeam(true);
    try {
      // No plan-selection step in this wizard - every new workgroup starts on
      // the active, unpaid plan (there's no reachable "Free" tier package today).
      const packages = await getPackages();
      const freePackage = packages.find((pkg) => pkg.isactive && !pkg.ispaid);
      if (!freePackage) {
        toast.error('Unable to set up your workgroup right now. Please contact support.');
        return;
      }

      if (!user) return;

      // industry/usecase mirror main's original signup flow: captured here for
      // client-side context only, same as CreateOrganisationData - neither has
      // a backend column to land on.
      const result = await createCompany(
        {
          title: workgroupTitle,
          contactpersonname: user.firstname || '',
          emailid: user.emailid || '',
          description: teamData.description,
          selectedPackage: freePackage.id,
        },
        { emailId: user.emailid, contactPersonName: user.firstname },
      );

      if (!result.success) {
        toast.error(result.error || 'Failed to set up your workgroup. Please try again.');
        return;
      }

      setCurrentStep(STEP_GITHUB_TOKEN);
    } catch {
      toast.error('Failed to save your team details. Please try again.');
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleSavePat = async () => {
    if (!GITHUB_PAT_PATTERN.test(patValue)) {
      setPatError('Enter a valid GitHub token (starts with "ghp_" or "github_pat_")');
      return;
    }

    setPatError(null);
    setIsSavingPat(true);
    try {
      const response = await PersonalAccessTokenAPI.save(patValue);
      setConnectedUsername(response.data?.username ?? null);
      setCurrentStep(STEP_READY);
    } catch {
      // Error is already surfaced via the global toast in apiClient.
    } finally {
      setIsSavingPat(false);
    }
  };

  const handleFinish = async () => {
    await refreshUser();
    onComplete();
  };

  if (currentStep === STEP_WELCOME) {
    return <Welcome onContinue={() => setCurrentStep(STEP_TEAM)} />;
  }

  const heading = STEP_HEADINGS[currentStep];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-card shadow-2xl rounded-lg border">
        <CardHeader className="border-b pb-6">
          <div className="flex items-center justify-center mb-4">
            <img
              src={theme === 'dark' ? orbitDarkLogo : orbitLogo}
              alt="ORbiT8"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl text-center">{heading.title}</CardTitle>
          <CardDescription className="text-center">{heading.subtitle}</CardDescription>

          <div className="flex items-center justify-center mt-4">
            {STEPPER_STEPS.map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step <= currentStep
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
                  }`}>
                  {index + 1}
                </div>
                {index < STEPPER_STEPS.length - 1 && (
                  <div className={`w-16 h-0.5 mx-2 ${step < currentStep ? 'bg-primary' : 'bg-muted'
                    }`} />
                )}
              </div>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-8">
          {currentStep === STEP_TEAM && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="flex items-center space-x-2 text-primary mb-6">
                <User className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Personal Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Name *</Label>
                  <Input
                    id="fullName"
                    value={user?.firstname || ''}
                    disabled
                    className="h-12 rounded-lg border bg-muted text-muted-foreground cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Profession/Field *</Label>
                  <Select onValueChange={(value) => setTeamData((prev) => ({ ...prev, industry: value }))}>
                    <SelectTrigger size="lg" className={`h-12 rounded-lg border focus:border-primary focus:ring-primary ${teamErrors.industry ? 'border-red-500' : ''
                      }`}>
                      <SelectValue placeholder="Select your field" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry.value} value={industry.value}>
                          {industry.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {teamErrors.industry && (
                    <div className="flex items-center text-red-500 text-sm">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {teamErrors.industry}
                    </div>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="usecase">How will you use ORbiT8? *</Label>
                  <Select onValueChange={(value) => setTeamData((prev) => ({ ...prev, usecase: value }))}>
                    <SelectTrigger size="lg" className={`h-12 rounded-lg border focus:border-primary focus:ring-primary ${teamErrors.usecase ? 'border-red-500' : ''
                      }`}>
                      <SelectValue placeholder="Select your use case" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal-projects">Personal Projects</SelectItem>
                      <SelectItem value="freelance-work">Freelance Work</SelectItem>
                      <SelectItem value="learning">Learning & Education</SelectItem>
                      <SelectItem value="research">Research & Development</SelectItem>
                      <SelectItem value="small-business">Small Business</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {teamErrors.usecase && (
                    <div className="flex items-center text-red-500 text-sm">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {teamErrors.usecase}
                    </div>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Tell us about your goals (Optional)</Label>
                  <Textarea
                    id="description"
                    value={teamData.description}
                    onChange={(e) => setTeamData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="What do you hope to achieve with ORbiT8..."
                    className="rounded-lg border focus:border-primary focus:ring-primary"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === STEP_GITHUB_TOKEN && (
            <div className="space-y-6 max-w-2xl mx-auto">
              <div className="flex items-center space-x-2 text-primary mb-6">
                <KeyRound className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Connect Your GitHub Token</h3>
              </div>

              {patError && (
                <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                  <AlertDescription className="text-red-800 dark:text-red-200">{patError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="githubPat">GitHub personal access token</Label>
                <div className="relative">
                  <Input
                    id="githubPat"
                    type={showPat ? 'text' : 'password'}
                    value={patValue}
                    onChange={(e) => { setPatValue(e.target.value); setPatError(null); }}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="pr-10 h-12 rounded-lg border-border focus:border-primary focus:ring-primary font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPat((prev) => !prev)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPat ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Requires the repo and read:org scopes. Create one in{' '}
                  <a
                    href="https://github.com/settings/tokens"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    GitHub developer settings
                  </a>.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                className="rounded-lg hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
                onClick={() => setShowPatHelpDialog(true)}
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                How to create a PAT
              </Button>
            </div>
          )}

          {currentStep === STEP_READY && (
            <div className="space-y-4 max-w-2xl mx-auto text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">Your workgroup is ready</h3>
                <p className="text-muted-foreground">
                  {connectedUsername && `Connected as ${connectedUsername}. `}
                  You can start scanning repositories and exploring ORbiT8.
                </p>
              </div>
            </div>
          )}
        </CardContent>

        <div className="border-t p-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep((prev) => Math.max(STEP_TEAM, prev - 1))}
            disabled={currentStep === STEP_TEAM}
            className="rounded-lg"
          >
            Previous
          </Button>

          <div className="flex space-x-2">
            {currentStep === STEP_TEAM && (
              <Button
                onClick={handleSaveTeam}
                disabled={isSavingTeam}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
              >
                {isSavingTeam ? 'Saving...' : 'Continue'}
              </Button>
            )}

            {currentStep === STEP_GITHUB_TOKEN && (
              <>
                <Button variant="ghost" onClick={() => setCurrentStep(STEP_READY)} className="rounded-lg">
                  Skip for now
                </Button>
                <Button
                  onClick={handleSavePat}
                  disabled={isSavingPat || !patValue}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                >
                  {isSavingPat ? 'Saving...' : 'Save & Continue'}
                </Button>
              </>
            )}

            {currentStep === STEP_READY && (
              <Button
                onClick={handleFinish}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            )}
          </div>
        </div>
      </Card>
      <MiniFooter />

      <Dialog open={showPatHelpDialog} onOpenChange={setShowPatHelpDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              GitHub Personal Access Token (PAT)
            </DialogTitle>
            <DialogDescription>
              A GitHub Personal Access Token is required only to scan repositories.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg bg-muted p-4 space-y-3">
              <div>
                <p className="font-semibold text-sm">Where to keep it in the tool</p>
                <p className="text-sm text-muted-foreground mt-1">
                  In <span className="font-medium text-foreground">Settings → Personal Access Token</span>, you can save a token so it's automatically used for every new GitHub scan.
                </p>
              </div>
            </div>

            <div>
              <p className="font-semibold text-sm mb-2">Option A — Fine-grained token (recommended)</p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                <li>Go to GitHub → Settings → Developer settings → Personal access tokens → <span className="font-medium text-foreground">Fine-grained tokens</span>.</li>
                <li>Click Generate new token.</li>
                <li>Under Repository permissions, set Contents to <span className="font-medium text-foreground">Read-only</span>.</li>
                <li>Copy the token and paste it into the GitHub PAT input field above.</li>
              </ol>
            </div>

            <div>
              <p className="font-semibold text-sm mb-2">Option B — Classic token</p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                <li>Go to GitHub → Settings → Developer settings → Personal access tokens → <span className="font-medium text-foreground">Tokens (classic)</span>.</li>
                <li>Click Generate new token (classic).</li>
                <li>Grant the repo scope (read access to private repositories).</li>
                <li>Copy the token and paste it into the GitHub PAT input field above.</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
              onClick={() => window.open('https://github.com/settings/tokens', '_blank')}
            >
              Open GitHub tokens
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <Button
              type="button"
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
              onClick={() => setShowPatHelpDialog(false)}
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AccountSetup;
